import ResponsiveAppBar from '@/components/ui/responsive-app-bar';
import {
  useCallback,
  PropsWithChildren,
  useState,
  useEffect,
  useRef
} from 'react';
import KeycloakInstance from '@/services/keycloak';
import {ConsoleLogger} from '@/services/logger';
import {useAppSelector} from '@/redux-store/store';
import LoadingScreen from '@/pages/loading-screen';
import {useSnackbar} from '@/contexts/snackbar';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {Company} from '@/types/company';
import {LogLevel} from '@/constants';
import {useMatrixContext} from '@/contexts/matrix/client';
import {env} from '@/config';
import {useDispatch} from 'react-redux';
import {fetchLocationLabel} from '@/services/filter/';
import {setLocations} from '@/redux-store/slices/filter';
import {
  setCompanySportsLoaded,
  setStartLocations
} from '@/redux-store/slices/auth';
import Footer from '../ui/footer';
import {Box} from '@mui/material';
import {getPaidEventId} from '@/rest/payments/get-payment-event-id';
import {getCompanyInfos} from '@/rest/company/get-company-infos';
import {setCompanyData} from '@/redux-store/slices/company';
import {setModules} from '@/redux-store/slices/modules';
import {getPublicCompanyTag} from '@/utils';

const logger = new ConsoleLogger(LogLevel.INFO);

type LayoutProps = PropsWithChildren<unknown>;

const Layout: React.FC<
  LayoutProps & {
    company?: Company;
    firstTranslationKey?: string;
    secondTranslationKey?: string;
    thirdTranslationKey?: string;
    skipBreadcrumb?: number;
  }
> = ({
  children,
  company,
  firstTranslationKey,
  secondTranslationKey,
  thirdTranslationKey,
  skipBreadcrumb
}) => {
  const {t} = useTranslation();

  const {APP_VARIANT, PUBLIC_COMPANIES} = env;

  const {showSnackbarMessage} = useSnackbar();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const hasFetchedRef = useRef(false);

  const goBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const [isLoading, setIsLoading] = useState(
    APP_VARIANT === 'sjone' ? true : false
  );
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isLocationFetched, setIsLocationFetched] = useState(false);
  const [homePath, setHomePath] = useState<string | null>(null);

  const ssoToken = useAppSelector((state) => state.auth.keycloakUser?.token);
  const {isAuthenticated, locationSet, isPublicCompany, currentCompanyId} =
    useAppSelector((state) => state.auth);
  const companyData = useAppSelector((state) => state.company.company);

  const companies = useAppSelector(
    (state) => state.userInfos.userInfos.companies || []
  );
  const sports = useAppSelector((state) => state.sports.sports);
  const userInfos = useAppSelector((state) => state.userInfos.userInfos);

  const {handleStopMatrixClient, startMatrixLoginProcess} = useMatrixContext();

  const handleLogin = useCallback(() => {
    KeycloakInstance.onErrorCallback = (error) => {
      if (error.message === 'Insufficient rights') {
        showSnackbarMessage(t('error_no_loginpermission'), 'error', 20000);
      }
    };
    if (!KeycloakInstance.authenticated) {
      KeycloakInstance.loginExternal().catch((error) => {
        logger.error('Login failed: ', error);
      });
    } else {
      logger.info('Already logged in');
    }
  }, [showSnackbarMessage, t]);

  const handleLogout = useCallback(() => {
    logger.info('logout');
    setIsLoggingOut(true);
    handleStopMatrixClient();
    KeycloakInstance.logoutExternal()
      .then(goBack)
      .catch((error) => {
        logger.error('Logout failed: ', error);
        setIsLoggingOut(false);
      });
  }, [goBack, handleStopMatrixClient]);

  useEffect(() => {
    const handlePaymentStatus = async () => {
      const query = new URLSearchParams(window.location.search);
      const paymentIntent = query.get('payment_intent');
      const redirectStatus = query.get('redirect_status');

      if (
        redirectStatus === 'succeeded' &&
        paymentIntent &&
        ssoToken &&
        KeycloakInstance.authenticated
      ) {
        try {
          if (ssoToken) {
            const {eventId} = await getPaidEventId(paymentIntent);

            if (eventId) {
              showSnackbarMessage(t('event_joining_success'), 'success');
              navigate(`/event/${eventId}`);
            } else {
              showSnackbarMessage(t('event_joining_failure'), 'error');
              navigate('/');
            }
          }
        } catch (error) {
          console.error('Error retrieving payment:', error);
          showSnackbarMessage(t('event_joining_failure'), 'error');
          navigate('/');
        }
      }
    };

    handlePaymentStatus();
  }, [navigate, showSnackbarMessage, t, ssoToken]);

  useEffect(() => {
    if (
      isAuthenticated &&
      !isLoggingOut &&
      ssoToken &&
      ssoToken !== 'undefined'
    ) {
      console.log('wanna start matrix login process');
      startMatrixLoginProcess();
    }
  }, [isAuthenticated, isLoggingOut, ssoToken, startMatrixLoginProcess]);

  useEffect(() => {
    if (isAuthenticated) {
      if (APP_VARIANT === 'sjone') {
        setIsLoading(companies.length === 0);
      } else {
        setIsLoading(Object.keys(sports).length === 0);
      }
    }
  }, [APP_VARIANT, companies, isAuthenticated, sports]);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'geolocation'
        });
        if (
          (permissionStatus.state === 'granted' ||
            permissionStatus.state === 'prompt') &&
          APP_VARIANT !== 'sjone'
        ) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const {latitude, longitude} = position.coords;
              const label = await fetchLocationLabel(latitude, longitude);
              localStorage.setItem(
                'location',
                JSON.stringify({latitude, longitude, label})
              );
              localStorage.setItem('locationAccess', 'granted');
              setIsLocationFetched(true);
            },
            (err) => {
              console.warn('Error obtaining location:', err);
              localStorage.setItem('locationAccess', 'denied');
            }
          );
        } else {
          localStorage.setItem('locationAccess', 'denied');
        }
      } catch (error) {
        console.error('Permission API not supported or error occurred:', error);
        localStorage.setItem('locationAccess', 'denied');
      }
    };

    fetchLocation();
  }, [APP_VARIANT]);

  useEffect(() => {
    const savedLocation = localStorage.getItem('location');
    if (APP_VARIANT !== 'sjone') {
      if (savedLocation) {
        const parsedLocation = savedLocation ? JSON.parse(savedLocation) : null;
        if (parsedLocation && !locationSet) {
          const {longitude, latitude, label} = parsedLocation;
          if (label && typeof label === 'string') {
            dispatch(setLocations({longitude, latitude, label}));
            dispatch(setStartLocations());
          }
        }
      } else if (userInfos.location) {
        const {lng, lat, label} = userInfos.location;
        if (lng && lat && label) {
          const longitude = lng;
          const latitude = lat;
          dispatch(setLocations({longitude, latitude, label}));
          dispatch(setStartLocations());
        }
      }
    }
  }, [
    dispatch,
    locationSet,
    isLocationFetched,
    userInfos.location,
    APP_VARIANT
  ]);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      if (
        isPublicCompany &&
        currentCompanyId &&
        !companyData._id &&
        !hasFetchedRef.current
      ) {
        hasFetchedRef.current = true;
        const fetchedCompanyData = await getCompanyInfos(
          currentCompanyId,
          true
        );
        if (
          fetchedCompanyData &&
          fetchedCompanyData._id &&
          fetchedCompanyData._id !== companyData._id
        ) {
          if (fetchedCompanyData?.theme?.navigation) {
            delete fetchedCompanyData.theme.navigation._id;
          }
          if (fetchedCompanyData?.theme?.titlebar) {
            delete fetchedCompanyData.theme.titlebar._id;
          }
          dispatch(setCompanyData(fetchedCompanyData));
          if (
            !fetchedCompanyData.allowedSports ||
            fetchedCompanyData.allowedSports.length === 0
          ) {
            dispatch(setCompanySportsLoaded(true));
          }
          const {modules} = fetchedCompanyData;
          if (modules && modules.length > 0) {
            dispatch(setModules(modules));
          }
          const companyTag = getPublicCompanyTag(
            currentCompanyId,
            PUBLIC_COMPANIES
          );
          if (companyTag) {
            setHomePath(`/${companyTag}`);
          }
        }
      }
      setIsLoading(false);
    };

    fetchCompanyInfo();
  }, [
    PUBLIC_COMPANIES,
    companyData._id,
    currentCompanyId,
    dispatch,
    isAuthenticated,
    isPublicCompany
  ]);

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      {(!isLoading || !isAuthenticated) && (
        <ResponsiveAppBar
          loginButtonOnClick={handleLogin}
          logoutButtonOnClick={handleLogout}
          company={company}
          firstTranslationKey={firstTranslationKey}
          secondTranslationKey={secondTranslationKey}
          thirdTranslationKey={thirdTranslationKey}
          skipBreadcrumb={skipBreadcrumb}
          breadcrumbHomePath={homePath || undefined}
        />
      )}
      {isAuthenticated && isLoading && <LoadingScreen />}

      <Box component="main" flexGrow={1}>
        {(isAuthenticated || isPublicCompany) &&
        !isLoading &&
        APP_VARIANT === 'sjone'
          ? children
          : !isLoading && APP_VARIANT === 'sportjoe'
          ? children
          : null}
      </Box>
      {!(isAuthenticated && isLoading) && <Footer />}
    </Box>
  );
};

export default Layout;
